/**
 * The runs list's scrape/enrichment split.
 *
 * **Why the page needed tabs.** Every external enrichment job gets its own
 * `runs` row — one per intake request or fan-out batch — so the count scales
 * with entity creation rather than with scrapes. On 2026-08-25 that was 5
 * enrichment runs and the split was deferred; re-measured on 2026-08-27, the
 * day the last cutover flag went live, it was **82 enrichment runs against 42
 * scrape runs in 48 h**, with an average of 1.0 events per enrichment run
 * against 749.5 per cron scrape. Single-event rows were burying the 749-event
 * runs a curator actually opens, on the most-used Tina page.
 */
import type { CrudFilters } from "@refinedev/core";
import dayjs from "dayjs";

import type { ManualTrigger, RunJobKind, TriggerSpec } from "@/types/tina";

export type { RunJobKind };

export interface RunsFilterState {
  /** `null` means "every class", i.e. send no `job_kind` at all. */
  jobKind: RunJobKind | null;
}

/**
 * `permanent` filters for the runs table.
 *
 * Same always-emit convention as `buildItemsFilters`: the field is present on
 * every render, with `value: undefined` when cleared, never omitted. Refine
 * builds its query as `unionFilters(permanent, internalState)` and drops
 * undefined entries *after* the dedupe, so a present-but-undefined entry shadows
 * a stale internal copy while an omitted one lets it survive.
 *
 * `job_kind` absent means both classes; an unknown *value* is a 422, which is
 * why the sentinel here is `null` and not a string. The param is live in prod
 * (`list_runs`, verified 2026-08-27 against 1394 scrape / 89 enrichment rows),
 * and both tabs send it explicitly, so the server does all of the filtering.
 *
 * **The rows are deliberately NOT re-checked client-side with {@link runJobKind},
 * even though the entity-trace lookup does exactly that for `entity_id`.** That
 * guard is safe because it re-verifies an unpaginated, entity-scoped fetch and
 * derives no count from it. This table is server-paginated: `useTable` renders
 * the server's `total`, so dropping rows from a page would leave a 25-row page
 * showing 3 and a pager promising pages that render empty — a wrong count where
 * the server's answer was already right.
 */
export const buildRunsFilters = ({ jobKind }: RunsFilterState): CrudFilters =>
  [
    { field: "job_kind", operator: "eq", value: jobKind ?? undefined },
  ] as CrudFilters;

/**
 * Which class a run belongs to, read off its `trigger_spec`.
 *
 * Mirrors Tina's three `RUN_*_KIND_EXPR` predicates, whose scrape arm is
 * `COALESCE(trigger_spec ->> 'job_kind', 'scrape') = 'scrape'` — the COALESCE is
 * load-bearing on both sides: `trigger_spec` predated `job_kind` on **1394 of
 * 1481** prod rows, so treating a missing marker as anything but "scrape"
 * empties the default tab. (Tina re-spelled its scrape arm from
 * `<> 'enrichment'` to `= 'scrape'` in migration `0058`, for exactly the reason
 * this function tests each marker positively: a third kind must not fall into
 * the scrape tab by default.)
 *
 * **`trigger_type` is never the discriminator.** It is only ever
 * manual/cron, so an enrichment run is typed `manual` and a refresh run
 * is typed `cron` — both are told apart only by the spec's own marker.
 */
export function runJobKind(spec: TriggerSpec | null | undefined): RunJobKind {
  if (spec && spec.type === "manual" && spec.job_kind === "enrichment") {
    return "enrichment";
  }
  if (spec && spec.type === "refresh") return "refresh";
  return "scrape";
}

/** What a refresh run's `trigger_spec` carries, or null for every other class. */
export interface RefreshRunSpec {
  /** The run's identity: `refresh_run_hour_key`, e.g. `"2026-09-03T01:00Z"`. */
  hour: string | null;
  /** The carry sites this batch crawled, if the sweep recorded any. */
  sites: string[];
}

/**
 * The refresh attribution off a run's `trigger_spec`.
 *
 * Deliberately typed off `spec.type === "refresh"` rather than
 * `job_kind === "refresh"`: both are always present together (tina's
 * `RefreshTrigger` fixes each as a `Literal`), and narrowing on `type` is what
 * gives TypeScript the variant.
 */
export function refreshRunSpec(
  spec: TriggerSpec | null | undefined,
): RefreshRunSpec | null {
  if (!spec || spec.type !== "refresh") return null;
  return { hour: spec.hour ?? null, sites: spec.sites ?? [] };
}

/**
 * A refresh run's hour, as a curator reads it: `"03/09 01:00"`.
 *
 * The key is a UTC hour floor and is rendered **as the local time of that
 * instant**, like every other timestamp on these pages — a run labelled with a
 * UTC hour beside a `created_at` in local time is two clocks in one row.
 */
export function refreshRunHourLabel(
  spec: TriggerSpec | null | undefined,
): string | null {
  const hour = refreshRunSpec(spec)?.hour;
  if (!hour) return null;
  const parsed = new Date(hour);
  if (Number.isNaN(parsed.getTime())) return hour;
  return dayjs(parsed).format("DD/MM HH:mm");
}

/** A dependent repair's parent entity, off `component_type` / `component_id`. */
export interface EnrichmentRunParent {
  entityType: string;
  entityId: string;
  /** The parent's own job row, so the run that caused this one stays one click away. */
  parentEventId: string | null;
}

/** The enrichment attribution a run's `trigger_spec` carries, or null. */
export interface EnrichmentRunSpec {
  source: string | null;
  entityCount: number | null;
  requestedBy: string | null;
  /** `"dependent_repair"`, or null on every other source. */
  reason: string | null;
  /** Non-null only for a repair run; see {@link EnrichmentRunParent}. */
  parent: EnrichmentRunParent | null;
}

export function enrichmentRunSpec(
  spec: TriggerSpec | null | undefined,
): EnrichmentRunSpec | null {
  if (!spec || spec.type !== "manual" || spec.job_kind !== "enrichment") {
    return null;
  }
  return {
    source: spec.source ?? null,
    entityCount:
      typeof spec.entity_count === "number" &&
      Number.isFinite(spec.entity_count)
        ? spec.entity_count
        : null,
    requestedBy: spec.requested_by ?? null,
    reason: spec.reason ?? null,
    parent: enrichmentRunParent(spec),
  };
}

/**
 * The parent entity of a dependent repair.
 *
 * Both halves are required: a `component_type` without an id (or the reverse)
 * cannot be linked anywhere, and rendering half of a reference is how a curator
 * ends up on a 404. `parent_event_id` is separately optional — two `bypass`
 * runs carry one with no component at all.
 */
function enrichmentRunParent(spec: ManualTrigger): EnrichmentRunParent | null {
  if (!spec.component_type || !spec.component_id) return null;
  return {
    entityType: spec.component_type,
    entityId: spec.component_id,
    parentEventId: spec.parent_event_id ?? null,
  };
}
